import React, { useRef, useCallback } from "react";
import dayjs from "dayjs";
import { IconInfoCircle } from "@tabler/icons-react";
import { Tooltip } from "antd";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import { usePortalSettings } from "_contexts/usePortalSettings";
import "./ProgramProgress.scss";

// Audio context singleton for click sounds
let audioContextRef = null;

function initAudio() {
  if (typeof window === "undefined") return;

  if (!audioContextRef) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      audioContextRef = new AudioContextClass();
    }
  }

  if (audioContextRef && audioContextRef.state === "suspended") {
    audioContextRef.resume().catch(() => {});
  }
}

function playTickSound() {
  try {
    initAudio();
    const ctx = audioContextRef;
    if (!ctx) return;

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    // Short "tick" sound
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(800, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.03);

    gainNode.gain.setValueAtTime(0.05, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.03);

    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.04);
  } catch (error) {
    // Ignore audio errors
  }
}

// Default APR for credit card debt (22% is a common average)
const DEFAULT_APR = 0.22;

/**
 * Calculate months to payoff using standard amortization formula
 * n = ln(p / (p - r*B)) / ln(1 + r)
 * 
 * @param {number} balance - Total debt balance
 * @param {number} apr - Annual percentage rate (e.g., 0.22 for 22%)
 * @param {number} monthlyPayment - Monthly payment amount
 * @returns {number} - Number of months to payoff
 */
const calculateMonthsToPayoff = (balance, apr, monthlyPayment) => {
  if (balance <= 0) return 0;
  
  const monthlyRate = apr / 12;
  const interestPortion = monthlyRate * balance;
  
  // If payment doesn't cover interest, debt will never be paid off
  if (monthlyPayment <= interestPortion) {
    return Infinity;
  }
  
  // Amortization formula: n = ln(p / (p - r*B)) / ln(1 + r)
  const n = Math.log(monthlyPayment / (monthlyPayment - monthlyRate * balance)) / Math.log(1 + monthlyRate);
  
  return Math.ceil(n);
};

/**
 * Calculate estimated minimum payment for credit card debt
 * Minimum payment = Interest for the month + 1% of balance
 * 
 * @param {number} balance - Total debt balance
 * @param {number} apr - Annual percentage rate
 * @returns {number} - Estimated minimum monthly payment
 */
const calculateMinimumPayment = (balance, apr) => {
  if (balance <= 0) return 0;
  
  const monthlyRate = apr / 12;
  const monthlyInterest = balance * monthlyRate;
  const onePercentOfBalance = balance * 0.01;
  
  // Minimum payment is typically interest + 1% of balance (or a minimum like $25)
  const calculatedMin = monthlyInterest + onePercentOfBalance;
  return Math.max(calculatedMin, 25); // Most cards have a minimum of $25
};

function ProgramProgress({ customer, payments = [] }) {
  const { getPrimaryColor } = usePortalSettings();
  const primaryColor = getPrimaryColor();

  // Track last played index for sound effect
  const lastPlayedIndex = useRef(null);

  // Handle mouse move for sound effect
  const handleMouseMove = useCallback((state) => {
    if (state && state.activeTooltipIndex !== undefined && state.activeTooltipIndex !== null) {
      if (lastPlayedIndex.current !== state.activeTooltipIndex) {
        playTickSound();
        // Haptic feedback for mobile
        if (navigator?.vibrate) {
          navigator.vibrate(5);
        }
        lastPlayedIndex.current = state.activeTooltipIndex;
      }
    } else {
      lastPlayedIndex.current = null;
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    lastPlayedIndex.current = null;
  }, []);

  // Calculate payment statistics
  const paidPayments = payments.filter(
    (p) => p.status === "paid" || p.status === "success"
  );

  // Calculate amounts
  const totalAmount = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const paidAmount = paidPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
  const remainingAmount = totalAmount - paidAmount;

  // Get total debt from plan
  const totalDebt = customer?.plan?.total_debt || customer?.plan?.debt_amount || 0;
  const savings = totalDebt > totalAmount ? totalDebt - totalAmount : 0;

  // Calculate timeline
  const today = dayjs();
  const lastPayment = payments.length > 0 
    ? dayjs(parseInt(payments[payments.length - 1]?.next_attempt)) 
    : dayjs().add(12, "month");
  const planEndDate = lastPayment;
  
  // "With plan" - months until plan completes
  const monthsWithPlan = Math.max(planEndDate.diff(today, "month"), 1);

  // "Without plan" - calculate using amortization formula
  // Use the total debt balance and calculate minimum payments
  const apr = DEFAULT_APR;
  const estimatedMinPayment = calculateMinimumPayment(totalDebt, apr);
  let monthsWithoutPlan = calculateMonthsToPayoff(totalDebt, apr, estimatedMinPayment);
  
  // Cap at reasonable maximum (30 years = 360 months)
  if (monthsWithoutPlan === Infinity || monthsWithoutPlan > 360) {
    monthsWithoutPlan = 360;
  }
  
  // Ensure without plan is always longer than with plan
  monthsWithoutPlan = Math.max(monthsWithoutPlan, monthsWithPlan + 6);
  
  const withoutPlanEndDate = today.add(monthsWithoutPlan, "month");
  const monthsSaved = monthsWithoutPlan - monthsWithPlan;

  // Generate chart data points with specific labels at key positions
  const generateChartData = () => {
    const data = [];
    const startingDebt = 100; // Normalized to 100%
    const numPoints = 20;

    // Find which point index is closest to the plan end
    const planEndPointIndex = Math.round((monthsWithPlan / monthsWithoutPlan) * numPoints);

    for (let i = 0; i <= numPoints; i++) {
      const monthProgress = i / numPoints;
      const currentMonth = Math.round(monthProgress * monthsWithoutPlan);
      
      // Calculate the date for this point
      const pointDate = today.add(currentMonth, "month");
      
      // "With plan" - drops to 0 at monthsWithPlan, stays at 0 after
      let withPlanValue;
      let withPlanAmount; // Actual dollar amount for tooltip
      if (currentMonth <= monthsWithPlan) {
        // Curved decline using quadratic easing
        const planProgress = currentMonth / monthsWithPlan;
        withPlanValue = startingDebt * Math.pow(1 - planProgress, 1.5);
        withPlanAmount = totalDebt * Math.pow(1 - planProgress, 1.5);
      } else {
        withPlanValue = 0;
        withPlanAmount = 0;
      }

      // "Without plan" - gradual decline based on actual amortization curve
      const withoutPlanProgress = currentMonth / monthsWithoutPlan;
      const withoutPlanValue = startingDebt * (1 - Math.pow(withoutPlanProgress, 0.7));
      const withoutPlanAmount = totalDebt * (1 - Math.pow(withoutPlanProgress, 0.7));

      // Determine label - only show at three key positions
      let label = "";
      if (i === 0) {
        label = "Today";
      } else if (i === planEndPointIndex) {
        label = planEndDate.format("MMM YYYY");
      } else if (i === numPoints) {
        label = withoutPlanEndDate.format("YYYY");
      }

      data.push({
        month: currentMonth,
        withPlan: Math.max(0, withPlanValue),
        withoutPlan: Math.max(0, withoutPlanValue),
        withPlanAmount: Math.max(0, Math.round(withPlanAmount)),
        withoutPlanAmount: Math.max(0, Math.round(withoutPlanAmount)),
        date: pointDate.format("MMM YYYY"),
        label,
        isPlanEnd: i === planEndPointIndex,
        isEnd: i === numPoints,
        index: i,
      });
    }

    return data;
  };

  const chartData = generateChartData();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Custom dot for the chart
  const CustomDot = (props) => {
    const { cx, cy, payload, dataKey } = props;
    
    // Show dot at plan end for "withPlan" line (where it hits 0)
    if (dataKey === "withPlan" && payload.isPlanEnd) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill={primaryColor}
          stroke="white"
          strokeWidth={2}
        />
      );
    }
    
    // Show dot at the end for "withoutPlan" line
    if (dataKey === "withoutPlan" && payload.isEnd) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={6}
          fill="white"
          stroke="#D1D5DB"
          strokeWidth={2}
        />
      );
    }
    
    return null;
  };

  // Create a unique gradient ID to avoid conflicts
  const gradientId = "programProgressGradient";

  // Custom tick for X-axis to align labels properly
  const CustomXAxisTick = (props) => {
    const { x, y, payload, index } = props;
    
    // Only render if there's a label
    if (!payload.value) return null;
    
    // Determine text anchor based on position
    let textAnchor = "middle";
    let xOffset = 0;
    
    if (index === 0) {
      // First label - align to start
      textAnchor = "start";
    } else if (index === chartData.length - 1) {
      // Last label - align to end
      textAnchor = "end";
    }
    
    return (
      <text
        x={x + xOffset}
        y={y + 12}
        textAnchor={textAnchor}
        fill="#78716C"
        fontSize={12}
      >
        {payload.value}
      </text>
    );
  };

  return (
    <div className="program-progress-panel">
      {/* Header with title and info button */}
      <div className="panel-header">
        <div className="panel-title-row">
          <h2 className="panel-title">Program Progress</h2>
        </div>
        <Tooltip title="We compare your proposed monthly payment against a standard minimum payment schedule to estimate how much time you'll save.">
          <button className="info-button">
            <IconInfoCircle size={18} />
          </button>
        </Tooltip>
      </div>

      {/* Subtitle with months saved */}
      <p className="progress-subtitle">
        It's estimated that this plan will help pay off your debt{" "}
        <span className="months-faster" style={{ color: primaryColor }}>
          {monthsSaved} months faster
        </span>
      </p>

      {/* Debt Progress Chart */}
      <div className="chart-container">
        <ResponsiveContainer width="100%" height={140}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 20, left: 20, bottom: 0 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={primaryColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={primaryColor} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={<CustomXAxisTick />}
              interval={0}
            />
            
            <YAxis hide domain={[0, 100]} />

            {/* Without plan - dashed line (rendered first so it's behind) */}
            <Area
              type="monotone"
              dataKey="withoutPlan"
              stroke="#D1D5DB"
              strokeWidth={2}
              strokeDasharray="6 4"
              fill="transparent"
              dot={<CustomDot dataKey="withoutPlan" />}
              activeDot={{ r: 5, strokeWidth: 2, stroke: "#D1D5DB", fill: "white" }}
              isAnimationActive={false}
            />

            {/* With plan - solid line with gradient fill */}
            <Area
              type="monotone"
              dataKey="withPlan"
              stroke={primaryColor}
              strokeWidth={2.5}
              fill={`url(#${gradientId})`}
              dot={<CustomDot dataKey="withPlan" />}
              activeDot={{ r: 6, strokeWidth: 0, fill: primaryColor }}
              isAnimationActive={false}
            />

            {/* Interactive Tooltip */}
            <RechartsTooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const item = payload[0]?.payload;
                  if (!item) return null;
                  return (
                    <div className="progress-chart-tooltip">
                      <p className="tooltip-date">{item.date}</p>
                      <p className="tooltip-plan">
                        <span className="tooltip-dot" style={{ backgroundColor: primaryColor }} />
                        Plan: {formatCurrency(item.withPlanAmount)}
                      </p>
                      <p className="tooltip-no-plan">
                        <span className="tooltip-dot tooltip-dot-gray" />
                        No Plan: {formatCurrency(item.withoutPlanAmount)}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
              cursor={{ stroke: primaryColor, strokeWidth: 1, strokeDasharray: "4 4", strokeOpacity: 0.5 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="chart-legend">
        <div className="legend-item">
          <span className="legend-dot" style={{ backgroundColor: primaryColor }} />
          <span className="legend-text">with plan</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot legend-dot-gray" />
          <span className="legend-text">without plan</span>
        </div>
      </div>

      {/* Divider */}
      <div className="section-divider" />

      {/* Stats Section */}
      <div className="stats-section">
        <div className="stats-row">
          <div className="stat-item">
            <span className="stat-label">Total</span>
            <span className="stat-value">{formatCurrency(totalAmount)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Paid</span>
            <span className="stat-value">{formatCurrency(paidAmount)}</span>
          </div>
        </div>
        <div className="stats-row stats-row-last">
          <div className="stat-item">
            <span className="stat-label">Remaining</span>
            <span className="stat-value">{formatCurrency(remainingAmount)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Savings</span>
            <span className="stat-value">{formatCurrency(savings)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProgramProgress;
